import {
  useDownloadCatalogSearchResultsQuery,
  type DownloadCatalogSearchResultsQueryVariables,
  type InstacartCustomersSharedV1Locale,
} from 'domains/products/api'
import { type ProductSearchParams } from 'domains/products/components/search/utils/types'
import {
  parseSearchTerm,
  parseCreationDate,
} from 'domains/products/api/getRetailerProductsMesh.hooks'
import { trackEvent } from 'utils/events/trackEvent'

interface UseDownloadCatalogSearchResultsProps {
  searchParams: ProductSearchParams
  requestProductData: boolean
  selectedKeys?: string[]
}

const mapParams = (
  props: UseDownloadCatalogSearchResultsProps
): DownloadCatalogSearchResultsQueryVariables => {
  const { searchParams, requestProductData, selectedKeys } = props
  const parsedCreationDate = parseCreationDate(searchParams)

  return {
    partnerId: searchParams.partnerId,
    retailerId: searchParams.retailerId,
    requestProductData: requestProductData,
    requestItemData: !requestProductData,
    localeCode: searchParams.locale.toUpperCase() as InstacartCustomersSharedV1Locale,
    aisleIds: searchParams.aisleId ? [searchParams.aisleId] : undefined,
    departmentIds: searchParams.departmentId ? [searchParams.departmentId] : undefined,
    primaryImageUrl:
      'hasPrimaryImageUrl' in searchParams ? searchParams.hasPrimaryImageUrl : undefined,
    creationDate: parsedCreationDate && {
      fromDate: parsedCreationDate.from,
      toDate: parsedCreationDate.to,
    },
    // if products are selected, don't send the search term -- use the selected product ids
    ...(selectedKeys && selectedKeys.length > 0 ? [] : parseSearchTerm(searchParams)),
    productIds: selectedKeys && selectedKeys.length > 0 ? selectedKeys : undefined,
  }
}

export const useDownloadCatalogSearchResults = (
  variables: UseDownloadCatalogSearchResultsProps,
  skip?: boolean
) => {
  const {
    data,
    error: apiError,
    loading: apiLoading,
    refetch: apiRefetch,
  } = useDownloadCatalogSearchResultsQuery({
    variables: mapParams(variables),
    skip,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
  })

  if (!skip) {
    trackEvent({
      id: 'products.products_search.download_event',
      description: 'Tracks individual download events',
      data: {
        partnerId: variables.searchParams.partnerId,
        retailerId: variables.searchParams.retailerId,
        locale: variables.searchParams.locale,
        departmentId: variables.searchParams.departmentId,
        aisleId: variables.searchParams.aisleId,
        requestDataType: variables.requestProductData ? 'product' : 'item',
      },
    })
  }

  return {
    apiResponse: data?.catalogToolsApiServiceDownloadCatalogSearchResults,
    apiError,
    apiLoading,
    apiRefetch,
  }
}
